import { query, execute } from "./client.js";
import { supabase, useSupabase } from "./supabaseClient.js";
import { ensureSchema } from "./init.js";
import { isTauri, invoke } from "@tauri-apps/api/core";

async function hashPassword(plain) {
  if (!plain) return "";
  try {
    const enc = new TextEncoder();
    const data = enc.encode(plain);
    const digest = await crypto.subtle.digest("SHA-256", data);
    const arr = Array.from(new Uint8Array(digest));
    return arr.map((b) => b.toString(16).padStart(2, "0")).join("");
  } catch {
    // Fallback simple hash
    let h = 0;
    for (let i = 0; i < plain.length; i++)
      h = (h << 5) - h + plain.charCodeAt(i);
    return String(h >>> 0);
  }
}

export function currentUser() {
  try {
    const raw = localStorage.getItem("sessionUser");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function logout() {
  localStorage.removeItem("sessionUser");
  localStorage.removeItem("sessionToken");
  // Notificar al UI que la sesión cambió para actualizar avatar/menus
  try {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("session-updated"));
    }
  } catch {}
}

export async function listRoles() {
  // Prioriza admin, luego cliente y otros
  if (useSupabase()) {
    const { data, error } = await supabase
      .from("roles")
      .select("id:id_roles,name")
      .order("name");
    if (error) return [];
    // Reordenar para priorizar roles comunes
    return (data || []).sort((a, b) => {
      const order = (n) =>
        n === "admin"
          ? 0
          : n === "cliente"
          ? 1
          : n === "proveedor"
          ? 2
          : n === "chef"
          ? 3
          : n === "mesero"
          ? 4
          : 9;
      const oa = order(String(a.name).toLowerCase());
      const ob = order(String(b.name).toLowerCase());
      if (oa !== ob) return oa - ob;
      return String(a.name).localeCompare(String(b.name));
    });
  }
  return query(`
    SELECT id_roles AS id, name
    FROM roles
    ORDER BY CASE name
      WHEN 'admin' THEN 0
      WHEN 'cliente' THEN 1
      WHEN 'proveedor' THEN 2
      WHEN 'chef' THEN 3
      WHEN 'mesero' THEN 4
      ELSE 9 END, name
  `);
}

// --- Gestión de usuarios y permisos (para apartado de administración) ---
export async function listUsers() {
  if (!isTauri() && !useSupabase()) {
    // Modo navegador: leer usuarios de localStorage si existen
    const users = JSON.parse(localStorage.getItem("dev_users") || "[]");
    return users.map((u) => ({
      id: u.id,
      username: u.username,
      full_name: u.full_name,
      email: u.email,
      role_name: u.role,
      role_id: null,
      is_active: u.is_active,
      is_verified: u.is_verified,
      avatar_url: u.avatar_url,
      created_at: null,
      bg_category: u.bg_category || null,
      theme_mode: u.theme_mode || null,
    }));
  }
  if (useSupabase()) {
    const { data, error } = await supabase
      .from("v_users_with_roles")
      .select("*")
      .order("username");
    if (error) return [];
    return (data || []).map((r) => ({
      id: r.id,
      username: r.username,
      full_name: r.full_name,
      email: r.email,
      avatar_url: r.avatar_url,
      bg_category: r.bg_category,
      theme_mode: r.theme_mode,
      is_verified: r.is_verified,
      is_active: r.is_active,
      created_at: r.created_at,
      role_name: r.role_name,
      role_id: r.role_id,
    }));
  }
  await ensureSchema();
  // Asegurar columnas que puedan faltar por instalaciones previas
  await ensureUserAvatarColumn();
  await ensureUserBgCategoryColumn();
  await ensureUserThemeModeColumn();
  const rows = await query(
    `SELECT u.id_users AS id, u.username, u.full_name, u.email, u.avatar_url, u.bg_category, u.theme_mode, u.is_verified, u.is_active,
            u.created_at, r.name AS role_name, r.id_roles AS role_id
     FROM users u
     LEFT JOIN roles r ON r.id_roles = u.role_id
     ORDER BY LOWER(u.username)`
  );
  return rows;
}

export async function setUserRole(userId, roleId) {
  if (!userId || roleId === undefined || roleId === null)
    return { ok: false, message: "Faltan parámetros" };
  if (useSupabase()) {
    const { error } = await supabase
      .from("users")
      .update({ role_id: Number(roleId) })
      .eq("id_users", Number(userId));
    if (error) return { ok: false, message: error.message };
    // Garantizar permisos mínimos por rol si no existen registros
    try {
      await ensureRoleDefaultPermissions(Number(roleId));
    } catch {}
    // Si se actualiza el rol del usuario en sesión, refrescar la sesión
    try {
      const session = currentUser();
      if (session && Number(session.id) === Number(userId)) {
        const { data } = await supabase
          .from("v_users_with_roles")
          .select(
            "id, username, full_name, email, avatar_url, bg_category, theme_mode, is_verified, role_id, role_name"
          )
          .eq("id", Number(userId))
          .limit(1);
        const u = (data && data[0]) || null;
        if (u) {
          const outUser = {
            id: u.id,
            username: u.username,
            full_name: u.full_name,
            email: u.email,
            avatar_url: u.avatar_url,
            bg_category: u.bg_category,
            theme_mode: u.theme_mode,
            is_verified: u.is_verified,
            role: u.role_name,
            role_id: u.role_id,
          };
          localStorage.setItem("sessionUser", JSON.stringify(outUser));
          if (typeof window !== "undefined") {
            window.dispatchEvent(new CustomEvent("session-updated"));
          }
        }
      }
    } catch {}
    return { ok: true };
  }
  if (!isTauri()) {
    const users = JSON.parse(localStorage.getItem("dev_users") || "[]");
    const roles = await listRoles();
    // Aceptar roleId como número o string
    const r = roles.find(
      (x) => x.id === Number(roleId) || String(x.id) === String(roleId)
    );
    const idx = users.findIndex((u) => u.id === userId);
    if (idx >= 0 && r) {
      users[idx].role = r.name;
      localStorage.setItem("dev_users", JSON.stringify(users));
      return { ok: true };
    }
    return { ok: false, message: "Usuario o rol no encontrado" };
  }
  try {
    await execute(`UPDATE users SET role_id = ? WHERE id_users = ?`, [
      Number(roleId),
      userId,
    ]);
    return { ok: true };
  } catch (e) {
    return { ok: false, message: e?.message || String(e) };
  }
}

// Asegura permisos mínimos por rol cuando role_permissions está vacío o todo en 0
export async function ensureRoleDefaultPermissions(roleId) {
  if (!useSupabase() || !roleId) return;
  const { data: roleRows, error: roleErr } = await supabase
    .from("roles")
    .select("id_roles, name")
    .eq("id_roles", Number(roleId))
    .limit(1);
  if (roleErr || !roleRows || !roleRows.length) return;
  const roleName = String(roleRows[0].name || "").toLowerCase();
  const mods = await listModules();
  const { data: existing, error } = await supabase
    .from("role_permissions")
    .select("module_id, can_access")
    .eq("role_id", Number(roleId));
  if (error) return; // sin permisos para leer, no intentamos escribir
  const noPerms =
    !existing ||
    existing.length === 0 ||
    existing.every((p) => Number(p.can_access || 0) === 0);
  if (!noPerms) return;
  const roleDefaults = {
    cliente: new Set(["dashboard", "products", "purchases", "menu"]),
    proveedor: new Set([
      "dashboard",
      "purchases",
      "suppliers",
      "inventory",
      "base_purchases",
    ]),
    chef: new Set([
      "dashboard",
      "inventory",
      "initial_inventory",
      "preparation",
    ]),
    mesero: new Set([
      "dashboard",
      "sales",
      "customers",
      "base_sales",
      "report_sales",
      "orders",
    ]),
  };
  const allowed = roleDefaults[roleName] || new Set(["dashboard"]);
  const rows = (mods || [])
    .filter((m) => allowed.has(m.code))
    .map((m) => ({
      role_id: Number(roleId),
      module_id: Number(m.id),
      can_access: 1,
    }));
  if (!rows.length) return;
  // upsert en bloque
  const { error: upErr } = await supabase
    .from("role_permissions")
    .upsert(rows, { onConflict: "role_id,module_id" });
  if (upErr) return;
}

export async function listModules() {
  if (useSupabase()) {
    const { data, error } = await supabase
      .from("modules")
      .select("id:id_modules, code, name, order_index")
      .order("order_index");
    if (error) {
      // Fallback mínimo si falla Supabase
      return [
        { id: 1, code: "dashboard", name: "Dashboard" },
        { id: 2, code: "inventory", name: "Inventario" },
        { id: 3, code: "sales", name: "Ventas" },
        { id: 4, code: "purchases", name: "Compras" },
        { id: 5, code: "customers", name: "Clientes" },
        { id: 6, code: "menu", name: "Menú" },
        { id: 7, code: "orders", name: "Órdenes" },
        { id: 8, code: "preparation", name: "Preparación" },
        { id: 99, code: "users", name: "Usuarios" },
      ];
    }
    const rows = data || [];
    // Si la tabla está vacía, devolver un conjunto mínimo para evitar UI vacía
    if (!rows.length) {
      return [
        { id: 1, code: "dashboard", name: "Dashboard" },
        { id: 2, code: "inventory", name: "Inventario" },
        { id: 3, code: "sales", name: "Ventas" },
        { id: 4, code: "purchases", name: "Compras" },
        { id: 5, code: "customers", name: "Clientes" },
        { id: 6, code: "menu", name: "Menú" },
        { id: 7, code: "orders", name: "Órdenes" },
        { id: 8, code: "preparation", name: "Preparación" },
        { id: 9, code: "products", name: "Productos" },
        { id: 99, code: "users", name: "Usuarios" },
      ];
    }
    if (!rows.some((r) => r.code === "users")) {
      rows.push({ id: -1, code: "users", name: "Usuarios", order_index: 999 });
    }
    if (!rows.some((r) => r.code === "invoicing")) {
      rows.push({ id: -2, code: "invoicing", name: "Facturación", order_index: 1000 });
    }
    if (!rows.some((r) => r.code === "updates")) {
      rows.push({ id: -3, code: "updates", name: "Actualizaciones", order_index: 1001 });
    }
    if (!rows.some((r) => r.code === "backup")) {
      rows.push({ id: -4, code: "backup", name: "Copia de Seguridad", order_index: 1002 });
    }
    return rows;
  }
  // Fallback si no usamos Supabase (modo dev navegador)
  return [
    { id: 1, code: "dashboard", name: "Dashboard" },
    { id: 2, code: "inventory", name: "Inventario" },
    { id: 3, code: "sales", name: "Ventas" },
    { id: 4, code: "purchases", name: "Compras" },
    { id: 5, code: "customers", name: "Clientes" },
    { id: 6, code: "menu", name: "Menú" },
    { id: 7, code: "orders", name: "Órdenes" },
    { id: 8, code: "preparation", name: "Preparación" },
    { id: 99, code: "users", name: "Usuarios" },
    { id: 100, code: "invoicing", name: "Facturación" },
    { id: 101, code: "updates", name: "Actualizaciones" },
    { id: 102, code: "backup", name: "Copia de Seguridad" },
  ];
}

export async function getRolePermissions(roleId) {
  if (!roleId) return [];
  if (useSupabase()) {
    const mods = await listModules();
    const { data: rpData, error } = await supabase
      .from("role_permissions")
      .select("module_id, can_access")
      .eq("role_id", Number(roleId));
    if (error) {
      return mods.map((m) => ({
        module_id: m.id,
        code: m.code,
        name: m.name,
        can_access: 0,
      }));
    }
    const map = new Map(
      (rpData || []).map((r) => [r.module_id, Number(r.can_access) ? 1 : 0])
    );
    return mods.map((m) => ({
      module_id: m.id,
      code: m.code,
      name: m.name,
      can_access: map.has(m.id) ? map.get(m.id) : 0,
    }));
  }
  if (!isTauri()) {
    // Modo navegador: reglas por defecto según rol
    const mods = await listModules();
    let roleName = "cliente";
    try {
      const roles = await listRoles();
      roleName = roles.find((r) => r.id === roleId)?.name || roleName;
    } catch {}
    const allowAll = roleName === "admin";
    const allowByRole = {
      cliente: new Set(["dashboard", "customers"]),
      proveedor: new Set([
        "dashboard",
        "purchases",
        "suppliers",
        "inventory",
        "base_purchases",
      ]),
      chef: new Set(["dashboard", "inventory", "initial_inventory"]),
      mesero: new Set([
        "dashboard",
        "sales",
        "customers",
        "base_sales",
        "report_sales",
      ]),
    };
    const allowed = allowByRole[roleName] || new Set();
    return mods.map((m) => ({
      module_id: m.id,
      code: m.code,
      name: m.name,
      can_access: allowAll ? 1 : allowed.has(m.code) ? 1 : 0,
    }));
  }
  const rows = await query(
    `SELECT m.id_modules AS module_id, m.code, m.name, IFNULL(rp.can_access, 0) AS can_access
     FROM modules m
     LEFT JOIN role_permissions rp ON rp.module_id = m.id_modules AND rp.role_id = ?
     ORDER BY m.order_index`,
    [roleId]
  );
  return rows;
}

export async function setRolePermission(roleId, moduleId, canAccess) {
  if (useSupabase()) {
    // Verificar que el módulo existe antes de insertar
    const { data: moduleExists, error: checkError } = await supabase
      .from("modules")
      .select("id_modules")
      .eq("id_modules", Number(moduleId))
      .single();
    
    if (checkError || !moduleExists) {
      console.warn(`⚠️ Módulo ${moduleId} no existe en la base de datos, ignorando permiso`);
      return { ok: false, message: `Módulo ${moduleId} no existe` };
    }
    
    const { error } = await supabase
      .from("role_permissions")
      .upsert(
        {
          role_id: Number(roleId),
          module_id: Number(moduleId),
          can_access: !!canAccess ? 1 : 0,
        },
        { onConflict: "role_id,module_id" }
      );
    return error ? { ok: false, message: error.message } : { ok: true };
  }
  if (!isTauri()) return { ok: true };
  try {
    await execute(
      `INSERT INTO role_permissions(role_id, module_id, can_access)
       VALUES (?,?,?)
       ON CONFLICT(role_id, module_id) DO UPDATE SET can_access = excluded.can_access`,
      [roleId, moduleId, canAccess ? 1 : 0]
    );
    return { ok: true };
  } catch (e) {
    return { ok: false, message: e?.message || String(e) };
  }
}

export async function deleteUser(userId) {
  if (!userId) return { ok: false, message: "Falta userId" };
  if (!isTauri()) {
    try {
      const users = JSON.parse(localStorage.getItem("dev_users") || "[]");
      const idx = users.findIndex((u) => u.id === userId);
      if (idx >= 0) {
        users.splice(idx, 1);
        localStorage.setItem("dev_users", JSON.stringify(users));
        // También eliminar overrides de permisos
        const ups = JSON.parse(
          localStorage.getItem("dev_user_permissions") || "[]"
        ).filter((p) => p.user_id !== userId);
        localStorage.setItem("dev_user_permissions", JSON.stringify(ups));
        return { ok: true };
      }
      return { ok: false, message: "Usuario no encontrado" };
    } catch (e) {
      return { ok: false, message: e?.message || String(e) };
    }
  }
  try {
    await execute(`DELETE FROM user_permissions WHERE user_id = ?`, [userId]);
    await execute(`DELETE FROM users WHERE id_users = ?`, [userId]);
    return { ok: true };
  } catch (e) {
    return { ok: false, message: e?.message || String(e) };
  }
}

// Limpiar overrides de permisos por usuario (deja solo los del rol)
export async function clearUserOverrides(userId) {
  if (!userId) return { ok: false, message: "Falta userId" };
  if (useSupabase()) {
    const { error } = await supabase
      .from("user_permissions")
      .delete()
      .eq("user_id", Number(userId));
    return error ? { ok: false, message: error.message } : { ok: true };
  }
  if (!isTauri()) {
    try {
      const ups = JSON.parse(
        localStorage.getItem("dev_user_permissions") || "[]"
      ).filter((p) => p.user_id !== userId);
      localStorage.setItem("dev_user_permissions", JSON.stringify(ups));
      return { ok: true };
    } catch (e) {
      return { ok: false, message: e?.message || String(e) };
    }
  }
  try {
    await execute(`DELETE FROM user_permissions WHERE user_id = ?`, [userId]);
    return { ok: true };
  } catch (e) {
    return { ok: false, message: e?.message || String(e) };
  }
}

// Actualizar datos del usuario (username, full_name, email, avatar_url, is_active)
export async function updateUser(userId, fields) {
  if (!userId || !fields || typeof fields !== "object") {
    return { ok: false, message: "Datos inválidos" };
  }
  const {
    username,
    full_name,
    email,
    avatar_url,
    bg_category,
    theme_mode,
    is_active,
  } = fields;
  // Rama Supabase: persistir cambios directamente en Postgres
  if (useSupabase()) {
    try {
      const payload = {};
      if (username !== undefined) payload.username = String(username);
      if (full_name !== undefined) payload.full_name = full_name || null;
      if (email !== undefined) payload.email = email || null;
      if (avatar_url !== undefined) payload.avatar_url = avatar_url || null;
      if (bg_category !== undefined) payload.bg_category = bg_category || null;
      if (theme_mode !== undefined) payload.theme_mode = theme_mode || null;
      if (is_active !== undefined) payload.is_active = !!is_active ? 1 : 0;
      if (Object.keys(payload).length === 0) return { ok: true };

      const { error } = await supabase
        .from("users")
        .update(payload)
        .eq("id_users", Number(userId));
      if (error) return { ok: false, message: error.message };

      // Refrescar datos de sesión si el usuario editado es el actual
      try {
        const session = currentUser();
        if (session && session.id === userId) {
          const { data, error: e2 } = await supabase
            .from("v_users_with_roles")
            .select("*")
            .eq("id", Number(userId))
            .limit(1);
          if (!e2 && data && data.length) {
            const r = data[0];
            const outUser = {
              id: r.id,
              username: r.username,
              full_name: r.full_name,
              email: r.email,
              avatar_url: r.avatar_url,
              bg_category: r.bg_category,
              theme_mode: r.theme_mode,
              is_verified: r.is_verified,
              role: r.role_name,
              role_id: r.role_id,
            };
            localStorage.setItem("sessionUser", JSON.stringify(outUser));
            if (typeof window !== "undefined") {
              window.dispatchEvent(new CustomEvent("session-updated"));
            }
          }
        }
      } catch {}
      return { ok: true };
    } catch (e) {
      return { ok: false, message: e?.message || String(e) };
    }
  }
  if (!isTauri()) {
    try {
      const users = JSON.parse(localStorage.getItem("dev_users") || "[]");
      const idx = users.findIndex((u) => u.id === userId);
      if (idx < 0) return { ok: false, message: "Usuario no encontrado" };
      // Evitar duplicar username si se cambia
      if (
        username &&
        users.some(
          (u) =>
            u.id !== userId &&
            String(u.username).toLowerCase() === String(username).toLowerCase()
        )
      ) {
        return { ok: false, message: "Usuario ya existe" };
      }
      const u = users[idx];
      if (username !== undefined) u.username = String(username);
      if (full_name !== undefined) u.full_name = full_name || null;
      if (email !== undefined) u.email = email || null;
      if (avatar_url !== undefined) u.avatar_url = avatar_url || null;
      if (bg_category !== undefined) u.bg_category = bg_category || null;
      if (theme_mode !== undefined) u.theme_mode = theme_mode || null;
      if (is_active !== undefined) u.is_active = !!is_active ? 1 : 0;
      localStorage.setItem("dev_users", JSON.stringify(users));
      // Si es el usuario en sesión, refrescar sessionUser y notificar
      try {
        const session = currentUser();
        if (session && session.id === userId) {
          const updated = {
            ...session,
            username: u.username,
            full_name: u.full_name,
            email: u.email,
            avatar_url: u.avatar_url,
            bg_category: u.bg_category,
            theme_mode: u.theme_mode,
          };
          localStorage.setItem("sessionUser", JSON.stringify(updated));
          if (typeof window !== "undefined") {
            window.dispatchEvent(new CustomEvent("session-updated"));
          }
        }
      } catch {}
      return { ok: true };
    } catch (e) {
      return { ok: false, message: e?.message || String(e) };
    }
  }
  try {
    await ensureSchema();
    await ensureUserAvatarColumn();
    await ensureUserBgCategoryColumn();
    await ensureUserThemeModeColumn();
    const sets = [];
    const params = [];
    if (username !== undefined) {
      sets.push("username = ?");
      params.push(String(username));
    }
    if (full_name !== undefined) {
      sets.push("full_name = ?");
      params.push(full_name || null);
    }
    if (email !== undefined) {
      sets.push("email = ?");
      params.push(email || null);
    }
    if (avatar_url !== undefined) {
      sets.push("avatar_url = ?");
      params.push(avatar_url || null);
    }
    if (bg_category !== undefined) {
      sets.push("bg_category = ?");
      params.push(bg_category || null);
    }
    if (theme_mode !== undefined) {
      sets.push("theme_mode = ?");
      params.push(theme_mode || null);
    }
    if (is_active !== undefined) {
      sets.push("is_active = ?");
      params.push(!!is_active ? 1 : 0);
    }
    if (!sets.length) return { ok: true };
    const sql = `UPDATE users SET ${sets.join(", ")} WHERE id_users = ?`;
    params.push(userId);
    await execute(sql, params);
    // Si el usuario editado está en sesión, refrescar datos en sessionUser
    try {
      const session = currentUser();
      if (session && session.id === userId) {
        const rows = await query(
          `SELECT u.id_users AS id, u.username, u.full_name, u.email, u.avatar_url, u.bg_category, u.theme_mode, u.is_verified, r.name AS role
           FROM users u
           LEFT JOIN roles r ON r.id_roles = u.role_id
           WHERE u.id_users = ?`,
          [userId]
        );
        if (rows.length) {
          localStorage.setItem("sessionUser", JSON.stringify(rows[0]));
          if (typeof window !== "undefined") {
            window.dispatchEvent(new CustomEvent("session-updated"));
          }
        }
      }
    } catch {}
    return { ok: true };
  } catch (e) {
    const msg = normalizeDbError(e);
    return { ok: false, message: msg };
  }
}

// Overrides de permisos por usuario
async function ensureUserPermissionsTable() {
  if (!isTauri()) return;
  try {
    await execute(`CREATE TABLE IF NOT EXISTS user_permissions (
      user_id INTEGER NOT NULL REFERENCES users(id_users) ON DELETE CASCADE,
      module_id INTEGER NOT NULL REFERENCES modules(id_modules) ON DELETE CASCADE,
      can_access INTEGER NOT NULL DEFAULT 1,
      PRIMARY KEY(user_id, module_id)
    )`);
  } catch {}
}

export async function getUserPermissions(userId) {
  if (!userId) return [];
  if (useSupabase()) {
    const mods = await listModules();
    const { data, error } = await supabase
      .from("user_permissions")
      .select("module_id, can_access")
      .eq("user_id", Number(userId));
    if (error) {
      return mods.map((m) => ({
        module_id: m.id,
        code: m.code,
        name: m.name,
        can_access: null,
      }));
    }
    const map = new Map(
      (data || []).map((r) => [r.module_id, Number(r.can_access)])
    );
    return mods.map((m) => ({
      module_id: m.id,
      code: m.code,
      name: m.name,
      can_access: map.has(m.id) ? map.get(m.id) : null,
    }));
  }
  if (!isTauri()) {
    const mods = await listModules();
    const ups = JSON.parse(
      localStorage.getItem("dev_user_permissions") || "[]"
    ).filter((p) => p.user_id === userId);
    const map = new Map(ups.map((p) => [p.module_id, p.can_access]));
    return mods.map((m) => ({
      module_id: m.id,
      code: m.code,
      name: m.name,
      can_access: map.has(m.id) ? map.get(m.id) : null,
    }));
  }
  await ensureSchema();
  await ensureUserPermissionsTable();
  const rows = await query(
    `SELECT m.id_modules AS module_id, m.code, m.name, up.can_access
     FROM modules m
     LEFT JOIN user_permissions up ON up.module_id = m.id_modules AND up.user_id = ?
     ORDER BY m.order_index`,
    [userId]
  );
  return rows;
}

export async function setUserPermission(userId, moduleId, canAccess) {
  if (!userId || !moduleId) return { ok: false, message: "Faltan datos" };
  if (useSupabase()) {
    // Verificar que el módulo existe antes de insertar
    const { data: moduleExists, error: checkError } = await supabase
      .from("modules")
      .select("id_modules")
      .eq("id_modules", Number(moduleId))
      .single();
    
    if (checkError || !moduleExists) {
      console.warn(`⚠️ Módulo ${moduleId} no existe en la base de datos, ignorando permiso`);
      return { ok: false, message: `Módulo ${moduleId} no existe` };
    }
    
    const { error } = await supabase
      .from("user_permissions")
      .upsert(
        {
          user_id: Number(userId),
          module_id: Number(moduleId),
          can_access: !!canAccess ? 1 : 0,
        },
        { onConflict: "user_id,module_id" }
      );
    return error ? { ok: false, message: error.message } : { ok: true };
  }
  if (!isTauri()) {
    const ups = JSON.parse(
      localStorage.getItem("dev_user_permissions") || "[]"
    );
    const idx = ups.findIndex(
      (p) => p.user_id === userId && p.module_id === moduleId
    );
    const val = !!canAccess ? 1 : 0;
    if (idx >= 0) ups[idx].can_access = val;
    else ups.push({ user_id: userId, module_id: moduleId, can_access: val });
    localStorage.setItem("dev_user_permissions", JSON.stringify(ups));
    return { ok: true };
  }
  try {
    await ensureUserPermissionsTable();
    await execute(
      `INSERT INTO user_permissions(user_id, module_id, can_access)
       VALUES (?,?,?)
       ON CONFLICT(user_id, module_id) DO UPDATE SET can_access = excluded.can_access`,
      [userId, moduleId, canAccess ? 1 : 0]
    );
    // Refrescar la sesión si el usuario editado es el que inició sesión
    try {
      const session = currentUser();
      if (session && session.id === userId) {
        const rows = await query(
          `SELECT u.id_users AS id, u.username, u.full_name, u.email, u.avatar_url, u.is_verified, r.name AS role
           FROM users u
           LEFT JOIN roles r ON r.id_roles = u.role_id
           WHERE u.id_users = ?`,
          [userId]
        );
        if (rows.length) {
          localStorage.setItem("sessionUser", JSON.stringify(rows[0]));
          if (typeof window !== "undefined") {
            window.dispatchEvent(new CustomEvent("session-updated"));
          }
        }
      }
    } catch {}
    return { ok: true };
  } catch (e) {
    return { ok: false, message: e?.message || String(e) };
  }
}

export async function getUserEffectivePermissions(userId) {
  if (!userId) return [];
  // Priorizar siempre Supabase si está configurado, incluso en Tauri
  if (useSupabase()) {
    const { data: urow } = await supabase
      .from("v_users_with_roles")
      .select("id, role_id, role_name")
      .eq("id", userId)
      .limit(1);
    const u = (urow && urow[0]) || null;
    const roleName = String(u?.role_name || "")
      .trim()
      .toLowerCase();
    // Admin/Superadmin: acceso total sin depender de role_permissions
    if (["admin", "superadmin"].includes(roleName)) {
      const mods = await listModules();
      let rows = (mods || []).map((m) => ({
        module_id: m.id,
        code: m.code,
        name: m.name,
        can_access: 1,
      }));
      // Respetar overrides en user_permissions si existen
      try {
        const { data: upsAdmin } = await supabase
          .from("user_permissions")
          .select("module_id, can_access")
          .eq("user_id", u?.id ?? userId);
        if (upsAdmin && upsAdmin.length) {
          const umap = new Map(
            upsAdmin.map((p) => [p.module_id, Number(p.can_access)])
          );
          rows = rows.map((r) =>
            umap.has(r.module_id)
              ? { ...r, can_access: umap.get(r.module_id) ? 1 : 0 }
              : r
          );
        }
      } catch {}
      return rows;
    }
    const mods = await listModules();
    const { data: perms, error: rpError } = await supabase
      .from("role_permissions")
      .select("module_id, can_access")
      .eq("role_id", u?.role_id ?? -1);
    // Fallback de permisos por defecto según rol cuando no hay registros o hay error de lectura
    const noPerms =
      rpError ||
      !perms ||
      perms.length === 0 ||
      perms.every((p) => Number(p.can_access || 0) === 0);
    if (noPerms) {
      // Para admin/superadmin: acceso completo por defecto, pero se respetan overrides
      if (["admin", "superadmin"].includes(roleName)) {
        let rows = (mods || []).map((m) => ({
          module_id: m.id,
          code: m.code,
          name: m.name,
          can_access: 1,
        }));
        const { data: upsAdmin } = await supabase
          .from("user_permissions")
          .select("module_id, can_access")
          .eq("user_id", u?.id ?? userId);
        if (upsAdmin && upsAdmin.length) {
          const umap = new Map(
            upsAdmin.map((p) => [p.module_id, Number(p.can_access)])
          );
          rows = rows.map((r) =>
            umap.has(r.module_id)
              ? { ...r, can_access: umap.get(r.module_id) ? 1 : 0 }
              : r
          );
        }
        return rows;
      }
      // Para roles no admin: por defecto todo en OFF; respetar overrides en user_permissions
      let rows = (mods || []).map((m) => ({
        module_id: m.id,
        code: m.code,
        name: m.name,
        can_access: 0,
      }));
      try {
        const { data: ups } = await supabase
          .from("user_permissions")
          .select("module_id, can_access")
          .eq("user_id", u?.id ?? userId);
        if (ups && ups.length) {
          const umap = new Map(
            ups.map((p) => [p.module_id, Number(p.can_access)])
          );
          rows = rows.map((r) =>
            umap.has(r.module_id)
              ? { ...r, can_access: umap.get(r.module_id) ? 1 : 0 }
              : r
          );
        }
      } catch {}
      return rows;
    }
    const map = new Map((perms || []).map((p) => [p.module_id, p.can_access]));
    let rows = (mods || []).map((m) => ({
      module_id: m.id,
      code: m.code,
      name: m.name,
      can_access: map.has(m.id) ? (map.get(m.id) ? 1 : 0) : 0,
    }));
    // Aplicar overrides por usuario desde user_permissions
    const { data: ups } = await supabase
      .from("user_permissions")
      .select("module_id, can_access")
      .eq("user_id", u?.id ?? userId);
    if (ups && ups.length) {
      const umap = new Map(ups.map((p) => [p.module_id, Number(p.can_access)]));
      rows = rows.map((r) =>
        umap.has(r.module_id)
          ? { ...r, can_access: umap.get(r.module_id) ? 1 : 0 }
          : r
      );
    }

    // FIX: Para rol 'cliente', el dashboard debe estar desactivado por defecto
    // (ignorando role_permissions) a menos que haya un override explícito en user_permissions.
    if (roleName === 'cliente') {
      const dash = rows.find((r) => r.code === 'dashboard');
      if (dash) {
        // Verificar si hubo override en ups
        const hasOverride = ups && ups.some((p) => p.module_id === dash.module_id);
        if (!hasOverride) {
          dash.can_access = 0;
        }
      }
    }

    // Refuerzo: mínimos y máximos por rol (no admin/superadmin)
    return rows;
  }
  // Navegador en modo dev (sin Supabase): usar localStorage
  if (!isTauri()) {
    const users = JSON.parse(localStorage.getItem("dev_users") || "[]");
    const u = users.find((x) => x.id === userId);
    const roles = await listRoles();
    const role = roles.find((r) => r.name === (u?.role || u?.role_name));
    const roleName = String(
      role?.name || u?.role || u?.role_name || ""
    ).toLowerCase();
    const base = ["admin", "superadmin"].includes(roleName)
      ? (await listModules()).map((m) => ({
          module_id: m.id,
          code: m.code,
          name: m.name,
          can_access: 1,
        }))
      : await getRolePermissions(role?.id);
    const ups = await getUserPermissions(userId);
    const map = new Map(ups.map((p) => [p.module_id, p.can_access]));
    const rows = base.map((b) => ({
      ...b,
      can_access:
        map.has(b.module_id) && map.get(b.module_id) !== null
          ? map.get(b.module_id)
            ? 1
            : 0
          : b.can_access,
    }));
    return rows;
  }
  const user = (
    await query(
      `SELECT u.id_users AS id, u.role_id, r.name AS role_name FROM users u LEFT JOIN roles r ON r.id_roles = u.role_id WHERE u.id_users = ?`,
      [userId]
    )
  )?.[0];
  if (!user) return [];
  await ensureUserPermissionsTable();
  // Admin y Superadmin: acceso completo a todos los módulos
  const roleName = String(user.role_name || "")
    .trim()
    .toLowerCase();
  if (["admin", "superadmin"].includes(roleName)) {
    const mods = await listModules();
    let rows = mods.map((m) => ({
      module_id: m.id,
      code: m.code,
      name: m.name,
      can_access: 1,
    }));
    const ups = await query(
      `SELECT module_id, can_access FROM user_permissions WHERE user_id = ?`,
      [user.id]
    );
    if (ups && ups.length) {
      const umap = new Map(ups.map((p) => [p.module_id, Number(p.can_access)]));
      rows = rows.map((r) =>
        umap.has(r.module_id)
          ? { ...r, can_access: umap.get(r.module_id) ? 1 : 0 }
          : r
      );
    }
    return rows;
  }
  let rows = await query(
    `SELECT m.id_modules AS module_id, m.code, m.name,
            COALESCE(up.can_access, rp.can_access, 0) AS can_access
     FROM modules m
     LEFT JOIN role_permissions rp ON rp.module_id = m.id_modules AND rp.role_id = ?
     LEFT JOIN user_permissions up ON up.module_id = m.id_modules AND up.user_id = ?
     ORDER BY m.order_index`,
    [user.role_id, user.id]
  );
  // Refuerzo: para rol cliente, asegurar mínimo acceso a dashboard/products/purchases/menu
  if (roleName === "cliente") {
    const minAllowed = new Set(["products", "purchases", "menu"]);
    rows = rows.map((r) =>
      minAllowed.has(String(r.code)) ? { ...r, can_access: 1 } : r
    );
  }
  return rows;
}
// ===== Funciones para verificar existencia de usuario/email =====
export async function checkUsernameExists(username) {
  if (!username) return { exists: false };
  const u = String(username).trim().toLowerCase();
  
  if (!isTauri() && !useSupabase()) {
    const users = JSON.parse(localStorage.getItem("dev_users") || "[]");
    const exists = users.some((user) => (user.username || "").toLowerCase() === u);
    return { exists };
  }
  
  if (useSupabase()) {
    const { data, error } = await supabase
      .from("users")
      .select("id_users, username")
      .ilike("username", u)
      .limit(1);
    if (error) return { exists: false, error: error.message };
    return { exists: (data && data.length > 0) };
  }
  
  // Tauri/SQLite
  const rows = await query(
    `SELECT id_users FROM users WHERE LOWER(username) = LOWER(?) LIMIT 1`,
    [u]
  );
  return { exists: rows.length > 0 };
}

export async function checkEmailExists(email) {
  if (!email) return { exists: false };
  const e = String(email).trim().toLowerCase();
  
  if (!isTauri() && !useSupabase()) {
    const users = JSON.parse(localStorage.getItem("dev_users") || "[]");
    const exists = users.some((user) => (user.email || "").toLowerCase() === e);
    return { exists };
  }
  
  if (useSupabase()) {
    const { data, error } = await supabase
      .from("users")
      .select("id_users, email")
      .ilike("email", e)
      .limit(1);
    if (error) return { exists: false, error: error.message };
    return { exists: (data && data.length > 0) };
  }
  
  // Tauri/SQLite
  const rows = await query(
    `SELECT id_users FROM users WHERE LOWER(email) = LOWER(?) LIMIT 1`,
    [e]
  );
  return { exists: rows.length > 0 };
}

export function generateUsernameSuggestions(baseUsername) {
  const suggestions = [];
  const base = String(baseUsername).trim().replace(/[^a-zA-Z0-9_]/g, '');
  if (!base) return suggestions;
  
  // Añadir números aleatorios
  for (let i = 0; i < 3; i++) {
    const rand = Math.floor(Math.random() * 9000) + 1000;
    suggestions.push(`${base}${rand}`);
  }
  // Añadir año
  suggestions.push(`${base}${new Date().getFullYear()}`);
  // Añadir guión bajo y número
  suggestions.push(`${base}_${Math.floor(Math.random() * 100)}`);
  
  return [...new Set(suggestions)].slice(0, 5);
}

export async function register({
  username,
  password,
  full_name,
  email,
  role_id,
  avatar_url,
  bg_category,
  theme_mode,
}) {
  if (!isTauri() && !useSupabase()) {
    try {
      const users = JSON.parse(localStorage.getItem("dev_users") || "[]");
      
      // Verificar usuario duplicado
      if (
        users.some(
          (u) =>
            (u.username || "").toLowerCase() === String(username).toLowerCase()
        )
      ) {
        return { 
          ok: false, 
          message: "Usuario ya existe",
          suggestions: generateUsernameSuggestions(username)
        };
      }
      
      // Verificar email duplicado
      if (email && users.some(
        (u) => (u.email || "").toLowerCase() === String(email).toLowerCase()
      )) {
        return { ok: false, message: "Este correo electrónico ya está registrado" };
      }
      
      const hash = await hashPassword(password);
      const code = String(Math.floor(100000 + Math.random() * 900000));
      const expiresIso = new Date(
        Date.now() + 24 * 60 * 60 * 1000
      ).toISOString();
      const isFirst = users.length === 0;
      const desiredRole = isFirst ? "admin" : "cliente";
      const defaultAvatar = `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(
        full_name || username || "User"
      )}`;
      const newUser = {
        id: (users.at(-1)?.id || 0) + 1,
        username: String(username),
        password_hash: hash,
        full_name: full_name || null,
        email: email || null,
        avatar_url: avatar_url || defaultAvatar,
        bg_category: bg_category || "nature",
        theme_mode: theme_mode || "dark",
        role: desiredRole,
        is_active: 1,
        is_verified: 0,
        activation_code: code,
        activation_expires: expiresIso,
      };
      users.push(newUser);
      localStorage.setItem("dev_users", JSON.stringify(users));
      if (email) await sendActivationEmail(email, full_name || username, code);
      return { ok: true, activation_code: code };
    } catch (e) {
      return { ok: false, message: "Error local de registro" };
    }
  }
  if (useSupabase()) {
    try {
      // ¿Usuario ya existe?
      const { data: uexists, error: eexists } = await supabase
        .from("users")
        .select("id_users")
        .ilike("username", String(username))
        .limit(1);
        
      if (eexists) {
        return { ok: false, message: `Error verificando usuario: ${eexists.message}` };
      }
      
      if (uexists && uexists.length) {
        return { 
          ok: false, 
          message: "Usuario ya existe",
          suggestions: generateUsernameSuggestions(username)
        };
      }
      
      // ¿Email ya existe?
      if (email) {
        const { data: emailExists, error: emailError } = await supabase
          .from("users")
          .select("id_users")
          .ilike("email", String(email))
          .limit(1);
        
        if (emailExists && emailExists.length) {
          return { ok: false, message: "Este correo electrónico ya está registrado" };
        }
      }


      const hash = await hashPassword(password);
      const code = String(Math.floor(100000 + Math.random() * 900000));
      const expiresIso = new Date(
        Date.now() + 24 * 60 * 60 * 1000
      ).toISOString();

      // Rol por defecto: primero admin, luego cliente
      let finalRoleId = role_id || null;
      if (!finalRoleId) {
        const { count, error: countError } = await supabase
          .from("users")
          .select("id_users", { count: "exact", head: true });
        
        const isFirst = Number(count || 0) === 0;
        const desiredRole = isFirst ? "admin" : "cliente";
        
        const { data: roleRow, error: roleError } = await supabase
          .from("roles")
          .select("id_roles")
          .eq("name", desiredRole)
          .limit(1)
          .single();
        
        finalRoleId = roleRow?.id_roles || null;
      }

      const defaultAvatar = `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(
        full_name || username || "User"
      )}`;
      
      const payload = {
        username: String(username),
        password_hash: hash,
        full_name: full_name || null,
        email: email || null,
        avatar_url: avatar_url || defaultAvatar,
        bg_category: bg_category || "nature",
        theme_mode: theme_mode || "dark",
        role_id: finalRoleId,
        is_active: 1,
        is_verified: 0,
        activation_code: code,
        activation_expires: expiresIso,
      };
      
      const { data: inserted, error: einsert } = await supabase
        .from("users")
        .insert([payload])
        .select("*")
        .single();
        
      if (einsert) {
        return { ok: false, message: `Error creando usuario: ${einsert.message || einsert.hint || 'Error desconocido'}` };
      }

      // Intentar enviar correo
      let mail_ok = false;
      let mail_error = "";
      if (email) {
        const m = await sendActivationEmail(email, full_name || username, code);
        mail_ok = !!m?.ok;
        mail_error = m?.message || "";
      }
      
      return {
        ok: true,
        activation_code: code,
        mail_ok,
        mail_error,
        id: inserted?.id_users,
      };
    } catch (e) {
      return {
        ok: false,
        message: e?.message || "Error de registro en Supabase",
      };
    }
  }
  try {
    // Asegurar que exista la tabla users (auto-inicialización si falta)
    try {
      const tbl = await query(
        "SELECT name FROM sqlite_master WHERE type='table' AND name='users'"
      );
      if (!tbl.length) {
        await ensureSchema();
      }
    } catch {}

    const exists = await query("SELECT 1 FROM users WHERE username = ?", [
      username,
    ]);
    if (exists.length) return { ok: false, message: "Usuario ya existe" };
    const hash = await hashPassword(password);
    // Asegurar columnas requeridas (para instalaciones existentes)
    await ensureUserAvatarColumn();
    await ensureUserBgCategoryColumn();
    await ensureUserThemeModeColumn();
    await ensureActivationColumns();
    // Rol automático: si es el primer usuario => admin; si no => cliente.
    let finalRoleId = role_id || null;
    if (!finalRoleId) {
      const cnt = await query("SELECT COUNT(*) AS c FROM users");
      const isFirst = Number(cnt?.[0]?.c || 0) === 0;
      const desired = isFirst ? "admin" : "cliente";
      const r = await query("SELECT id_roles AS id FROM roles WHERE name = ?", [
        desired,
      ]);
      finalRoleId = r?.[0]?.id || null;
      // Fallback suave si faltara el rol "cliente": usar admin para no bloquear
      if (!finalRoleId && !isFirst) {
        const rv = await query(
          "SELECT id_roles AS id FROM roles WHERE name = ?",
          ["cliente"]
        );
        finalRoleId = rv?.[0]?.id || null;
      }
    }
    const defaultAvatar = `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(
      full_name || username || "User"
    )}`;
    // ¿Existe columna avatar_url y verificación?
    const cols = await query(`PRAGMA table_info(users)`);
    const hasAvatar = cols.some((c) => (c.name || c["name"]) === "avatar_url");
    const hasVerification = cols.some(
      (c) => (c.name || c["name"]) === "is_verified"
    );
    const hasBgCat = cols.some((c) => (c.name || c["name"]) === "bg_category");
    const hasTheme = cols.some((c) => (c.name || c["name"]) === "theme_mode");
    const code = String(Math.floor(100000 + Math.random() * 900000)); // 6 dígitos
    const expiresIso = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
    if (hasAvatar) {
      if (hasVerification) {
        if (hasBgCat) {
          if (hasTheme) {
            await execute(
              `INSERT INTO users(username, password_hash, full_name, email, avatar_url, bg_category, theme_mode, is_verified, activation_code, activation_expires, role_id, is_active)
               VALUES (?, ?, ?, ?, ?, ?, ?, 0, ?, ?, ?, 1)`,
              [
                username,
                hash,
                full_name || null,
                email || null,
                avatar_url || defaultAvatar,
                bg_category || "nature",
                theme_mode || "dark",
                code,
                expiresIso,
                finalRoleId,
              ]
            );
          } else {
            await execute(
              `INSERT INTO users(username, password_hash, full_name, email, avatar_url, bg_category, is_verified, activation_code, activation_expires, role_id, is_active)
               VALUES (?, ?, ?, ?, ?, ?, 0, ?, ?, ?, 1)`,
              [
                username,
                hash,
                full_name || null,
                email || null,
                avatar_url || defaultAvatar,
                bg_category || "nature",
                code,
                expiresIso,
                finalRoleId,
              ]
            );
          }
        } else {
          if (hasTheme) {
            await execute(
              `INSERT INTO users(username, password_hash, full_name, email, avatar_url, theme_mode, is_verified, activation_code, activation_expires, role_id, is_active)
               VALUES (?, ?, ?, ?, ?, ?, 0, ?, ?, ?, 1)`,
              [
                username,
                hash,
                full_name || null,
                email || null,
                avatar_url || defaultAvatar,
                theme_mode || "dark",
                code,
                expiresIso,
                finalRoleId,
              ]
            );
          } else {
            await execute(
              `INSERT INTO users(username, password_hash, full_name, email, avatar_url, is_verified, activation_code, activation_expires, role_id, is_active)
               VALUES (?, ?, ?, ?, ?, 0, ?, ?, ?, 1)`,
              [
                username,
                hash,
                full_name || null,
                email || null,
                avatar_url || defaultAvatar,
                code,
                expiresIso,
                finalRoleId,
              ]
            );
          }
        }
      } else {
        if (hasBgCat) {
          if (hasTheme) {
            await execute(
              `INSERT INTO users(username, password_hash, full_name, email, avatar_url, bg_category, theme_mode, role_id, is_active)
               VALUES (?, ?, ?, ?, ?, ?, ?, ?, 1)`,
              [
                username,
                hash,
                full_name || null,
                email || null,
                avatar_url || defaultAvatar,
                bg_category || "nature",
                theme_mode || "dark",
                finalRoleId,
              ]
            );
          } else {
            await execute(
              `INSERT INTO users(username, password_hash, full_name, email, avatar_url, bg_category, role_id, is_active)
               VALUES (?, ?, ?, ?, ?, ?, ?, 1)`,
              [
                username,
                hash,
                full_name || null,
                email || null,
                avatar_url || defaultAvatar,
                bg_category || "nature",
                finalRoleId,
              ]
            );
          }
        } else {
          if (hasTheme) {
            await execute(
              `INSERT INTO users(username, password_hash, full_name, email, avatar_url, theme_mode, role_id, is_active)
               VALUES (?, ?, ?, ?, ?, ?, ?, 1)`,
              [
                username,
                hash,
                full_name || null,
                email || null,
                avatar_url || defaultAvatar,
                theme_mode || "dark",
                finalRoleId,
              ]
            );
          } else {
            await execute(
              `INSERT INTO users(username, password_hash, full_name, email, avatar_url, role_id, is_active)
               VALUES (?, ?, ?, ?, ?, ?, 1)`,
              [
                username,
                hash,
                full_name || null,
                email || null,
                avatar_url || defaultAvatar,
                finalRoleId,
              ]
            );
          }
        }
      }
    } else {
      if (hasBgCat) {
        if (hasTheme) {
          await execute(
            `INSERT INTO users(username, password_hash, full_name, email, bg_category, theme_mode, role_id, is_active)
             VALUES (?, ?, ?, ?, ?, ?, ?, 1)`,
            [
              username,
              hash,
              full_name || null,
              email || null,
              bg_category || "nature",
              theme_mode || "dark",
              finalRoleId,
            ]
          );
        } else {
          await execute(
            `INSERT INTO users(username, password_hash, full_name, email, bg_category, role_id, is_active)
             VALUES (?, ?, ?, ?, ?, ?, 1)`,
            [
              username,
              hash,
              full_name || null,
              email || null,
              bg_category || "nature",
              finalRoleId,
            ]
          );
        }
      } else {
        if (hasTheme) {
          await execute(
            `INSERT INTO users(username, password_hash, full_name, email, theme_mode, role_id, is_active)
             VALUES (?, ?, ?, ?, ?, ?, 1)`,
            [
              username,
              hash,
              full_name || null,
              email || null,
              theme_mode || "dark",
              finalRoleId,
            ]
          );
        } else {
          await execute(
            `INSERT INTO users(username, password_hash, full_name, email, role_id, is_active)
             VALUES (?, ?, ?, ?, ?, 1)`,
            [username, hash, full_name || null, email || null, finalRoleId]
          );
        }
      }
    }
    // Intentar enviar correo con el código y reportar estado
    let mail_ok = false;
    let mail_error = "";
    if (email) {
      const m = await sendActivationEmail(email, full_name || username, code);
      mail_ok = !!m?.ok;
      mail_error = m?.message || "";
    }
    return { ok: true, activation_code: code, mail_ok, mail_error };
  } catch (e) {
    const msg = normalizeDbError(e);
    return { ok: false, message: msg };
  }
}

function normalizeDbError(e) {
  try {
    const raw = (e?.message || String(e) || "").toLowerCase();
    if (raw.includes("no such table: users"))
      return "Falta la tabla users. Ejecuta el schema: ensureSchema()";
    if (raw.includes("no such table"))
      return "Faltan tablas en la base de datos. Aplica el schema.";
    if (raw.includes("no such column"))
      return "La base no tiene una columna requerida. Aplica el schema.";
    if (raw.includes("unique constraint failed: users.username"))
      return "Usuario ya existe";
    if (raw.includes("database is locked"))
      return "Base de datos bloqueada. Cierra otras instancias y reintenta.";
    if (raw.includes("permission") || raw.includes("capability"))
      return "Permisos del plugin SQL no concedidos.";
    return e?.message || "Error interno en registro";
  } catch {
    return "Error interno en registro";
  }
}

export async function login({ username, password }) {
  const hash = await hashPassword(password);
  if (!isTauri() && !useSupabase()) {
    const users = JSON.parse(localStorage.getItem("dev_users") || "[]");
    const u = users.find(
      (u) =>
        u.username === String(username) &&
        u.password_hash === hash &&
        u.is_active === 1
    );
    if (!u) return { ok: false, message: "Credenciales inválidas" };
    if (u.is_verified !== 1) {
      return {
        ok: false,
        message: "Cuenta no activada. Revisa tu email y activa tu cuenta.",
      };
    }
    const user = {
      id: u.id,
      username: u.username,
      full_name: u.full_name,
      email: u.email,
      avatar_url: u.avatar_url,
      bg_category: u.bg_category,
      theme_mode: u.theme_mode,
      is_verified: u.is_verified,
      role: u.role,
    };
    localStorage.setItem("sessionUser", JSON.stringify(user));
    const token = await signJWT(
      { id: user.id, username: user.username, role: user.role },
      60 * 60 * 24
    );
    localStorage.setItem("sessionToken", token);
    try {
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("session-updated"));
      }
    } catch {}
    return { ok: true, user };
  }
  if (useSupabase()) {
    const { data, error } = await supabase
      .from("v_users_with_roles")
      .select("*")
      .eq("username", username)
      .eq("password_hash", hash)
      .eq("is_active", 1)
      .limit(1);
    if (error) return { ok: false, message: "Error de autenticación" };
    if (!data || !data.length)
      return { ok: false, message: "Credenciales inválidas" };
    const user = data[0];
    if (user.is_verified !== 1) {
      return {
        ok: false,
        message: "Cuenta no activada. Revisa tu email y activa tu cuenta.",
      };
    }
    const outUser = {
      id: user.id,
      username: user.username,
      full_name: user.full_name,
      email: user.email,
      avatar_url: user.avatar_url,
      bg_category: user.bg_category,
      theme_mode: user.theme_mode,
      is_verified: user.is_verified,
      role: user.role_name,
      role_id: user.role_id,
    };
    localStorage.setItem("sessionUser", JSON.stringify(outUser));
    const token = await signJWT(
      { id: outUser.id, username: outUser.username, role: outUser.role },
      60 * 60 * 24
    );
    localStorage.setItem("sessionToken", token);
    try {
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("session-updated"));
      }
    } catch {}
    return { ok: true, user: outUser };
  }
  const rows = await query(
    `SELECT u.id_users AS id, u.username, u.full_name, u.email, u.avatar_url, u.bg_category, u.theme_mode, u.is_verified, r.name AS role
     FROM users u
     LEFT JOIN roles r ON r.id_roles = u.role_id
     WHERE u.username = ? AND u.password_hash = ? AND u.is_active = 1`,
    [username, hash]
  );
  if (!rows.length) return { ok: false, message: "Credenciales inválidas" };
  const user = rows[0];
  if (user.is_verified !== 1) {
    return {
      ok: false,
      message: "Cuenta no activada. Revisa tu email y activa tu cuenta.",
    };
  }
  localStorage.setItem("sessionUser", JSON.stringify(user));
  const token = await signJWT(
    { id: user.id, username: user.username, role: user.role },
    60 * 60 * 24
  ); // 24h
  localStorage.setItem("sessionToken", token);
  try {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("session-updated"));
    }
  } catch {}
  return { ok: true, user };
}

export async function modulesForCurrentUser() {
  const user = currentUser();
  // En modo navegador o sin sesión: mostrar módulos básicos
  if (!user) {
    return [
      { code: "inventory", name: "Inventario", icon: "inventory_2" },
      { code: "sales", name: "Ventas", icon: "point_of_sale" },
      { code: "purchases", name: "Compras", icon: "shopping_bag" },
      { code: "customers", name: "Clientes", icon: "group" },
      { code: "products", name: "Productos", icon: "category" },
      { code: "menu", name: "Menú", icon: "restaurant_menu" },
    ];
  }
  // Admin/Superadmin: devolver todos los módulos sin depender de permisos
  const role = String(user.role || "").toLowerCase();
  const isAdmin =
    role === "admin" || role === "superadmin" || user?.role_id === 1;
  if (isAdmin) {
    const eff = await getUserEffectivePermissions(user.id);
    if (eff && eff.length) {
      return eff
        .filter((e) => Number(e.can_access || 0) === 1)
        .map((e) => ({ code: e.code, name: e.name, icon: pickIcon(e.code) }));
    }
    const mods = await listModules();
    return (mods || []).map((m) => ({ code: m.code, name: m.name, icon: pickIcon(m.code) }));
  }
  // Usar permisos efectivos por usuario (override sobre rol)
  const eff = await getUserEffectivePermissions(user.id);
  // Fallback: si el rol es cliente y no hay permisos o todos son 0, habilitar dashboard, products, purchases y menú
  const roleName = String(user.role || "").toLowerCase();
  const noPerms =
    !eff ||
    eff.length === 0 ||
    eff.every((e) => Number(e.can_access || 0) === 0);
  let baseRows = eff;
  if (roleName === "cliente" && noPerms) {
    const allowed = new Set(["products", "purchases", "menu"]);
    const allMods = await listModules();
    baseRows = (allMods || []).map((m) => ({
      code: m.code,
      name: m.name,
      can_access: allowed.has(m.code) ? 1 : 0,
    }));
  }
  const rows = (baseRows || [])
    .filter((e) => e.can_access === 1)
    .map((e) => ({ code: e.code, name: e.name }));
  let mods = rows.map((r) => ({
    code: r.code,
    name: r.name,
    icon: pickIcon(r.code),
  }));
  // Asegurar que admin vea el módulo de Usuarios aunque no exista en la tabla
  // isAdmin ya calculado arriba
  // Solo admin puede acceder a "Usuarios"
  if (!isAdmin) {
    mods = mods.filter((m) => m.code !== "users");
  }
  if (isAdmin) {
    const hasUsers = mods.some((m) => m.code === "users");
    if (!hasUsers)
      mods.push({ code: "users", name: "Usuarios", icon: pickIcon("users") });
  }
  return mods;
}

function pickIcon(code) {
  const map = {
    dashboard: "space_dashboard",
    inventory: "inventory_2",
    sales: "point_of_sale",
    purchases: "shopping_bag",
    initial_inventory: "playlist_add_check",
    taxes: "request_quote",
    suppliers: "store",
    customers: "group",
    products: "category",
    menu: "restaurant_menu",
    orders: "receipt_long",
    preparation: "soup_kitchen",
    report_sales: "insights",
    daily_sales: "calendar_month",
    daily_profits: "trending_up",
    base_purchases: "storage",
    base_sales: "storage",
  };
  return map[code] || "apps";
}

// ===== JWT Helpers =====
function base64url(str) {
  return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+/g, "");
}

function base64urlFromBytes(bytes) {
  let bin = "";
  bytes.forEach((b) => (bin += String.fromCharCode(b)));
  return base64url(bin);
}

export async function getJwtSecret() {
  // Con Supabase, no dependemos de SQLite para leer el secreto.
  // Usamos un valor por defecto estable y seguro para firmar/verificar durante la sesión local.
  try {
    if (!isTauri()) return "dev-secret";
    if (useSupabase()) return "dev-secret";
    const rows = await query(
      `SELECT value FROM settings WHERE key = 'jwt_secret'`
    );
    return rows?.[0]?.value || "dev-secret";
  } catch {
    return "dev-secret";
  }
}

export async function signJWT(payload, expiresInSeconds = 3600) {
  const header = { alg: "HS256", typ: "JWT" };
  const exp = Math.floor(Date.now() / 1000) + expiresInSeconds;
  const data = { ...payload, exp };
  const encHeader = base64url(JSON.stringify(header));
  const encPayload = base64url(JSON.stringify(data));
  const secret = await getJwtSecret();
  const key = await crypto.subtle.importKey(
    "raw",
    new TextEncoder().encode(secret),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"]
  );
  const sigBuf = await crypto.subtle.sign(
    "HMAC",
    key,
    new TextEncoder().encode(`${encHeader}.${encPayload}`)
  );
  const signature = base64urlFromBytes(new Uint8Array(sigBuf));
  return `${encHeader}.${encPayload}.${signature}`;
}

export async function verifyJWT(token) {
  try {
    const [h, p, s] = token.split(".");
    if (!h || !p || !s) return { ok: false };
    const secret = await getJwtSecret();
    const key = await crypto.subtle.importKey(
      "raw",
      new TextEncoder().encode(secret),
      { name: "HMAC", hash: "SHA-256" },
      false,
      ["sign"]
    );
    const sigBuf = await crypto.subtle.sign(
      "HMAC",
      key,
      new TextEncoder().encode(`${h}.${p}`)
    );
    const expected = base64urlFromBytes(new Uint8Array(sigBuf));
    if (expected !== s) return { ok: false };
    const payload = JSON.parse(atob(p.replace(/-/g, "+").replace(/_/g, "/")));
    if (payload.exp && payload.exp < Math.floor(Date.now() / 1000))
      return { ok: false };
    return { ok: true, payload };
  } catch {
    return { ok: false };
  }
}

export async function isAuthenticated() {
  const token = localStorage.getItem("sessionToken");
  if (!token) return false;
  const res = await verifyJWT(token);
  return !!res.ok;
}

async function ensureUserAvatarColumn() {
  try {
    // Verificar si existe avatar_url
    const cols = await query(`PRAGMA table_info(users)`);
    const hasAvatar = cols.some((c) => (c.name || c["name"]) === "avatar_url");
    if (!hasAvatar) {
      await execute(`ALTER TABLE users ADD COLUMN avatar_url TEXT`);
    }
  } catch (e) {
    // Ignorar errores (p.ej. navegador o ya agregado)
    return;
  }
}

async function ensureUserBgCategoryColumn() {
  try {
    const cols = await query(`PRAGMA table_info(users)`);
    const hasBg = cols.some((c) => (c.name || c["name"]) === "bg_category");
    if (!hasBg) {
      await execute(`ALTER TABLE users ADD COLUMN bg_category TEXT`);
    }
  } catch (e) {
    return;
  }
}

async function ensureUserThemeModeColumn() {
  try {
    const cols = await query(`PRAGMA table_info(users)`);
    const hasTheme = cols.some((c) => (c.name || c["name"]) === "theme_mode");
    if (!hasTheme) {
      await execute(`ALTER TABLE users ADD COLUMN theme_mode TEXT`);
    }
  } catch (e) {
    return;
  }
}

async function ensureActivationColumns() {
  try {
    const cols = await query(`PRAGMA table_info(users)`);
    const names = cols.map((c) => c.name || c["name"]);
    if (!names.includes("is_verified")) {
      await execute(
        `ALTER TABLE users ADD COLUMN is_verified INTEGER NOT NULL DEFAULT 0`
      );
    }
    if (!names.includes("activation_code")) {
      await execute(`ALTER TABLE users ADD COLUMN activation_code TEXT`);
    }
    if (!names.includes("activation_expires")) {
      await execute(`ALTER TABLE users ADD COLUMN activation_expires TEXT`);
    }
  } catch {}
}

export async function activateAccount({ username, code }) {
  if (!isTauri() && !useSupabase()) {
    const users = JSON.parse(localStorage.getItem("dev_users") || "[]");
    const idx = users.findIndex((u) => u.username === String(username));
    if (idx < 0) return { ok: false, message: "Usuario no encontrado" };
    const u = users[idx];
    const ok =
      !!u.activation_code && String(u.activation_code) === String(code);
    const notExpired =
      !u.activation_expires ||
      new Date(u.activation_expires).getTime() > Date.now();
    if (!ok) return { ok: false, message: "Código inválido" };
    if (!notExpired) return { ok: false, message: "Código expirado" };
    users[idx].is_verified = 1;
    users[idx].activation_code = null;
    users[idx].activation_expires = null;
    localStorage.setItem("dev_users", JSON.stringify(users));
    return { ok: true };
  }
  if (useSupabase()) {
    try {
      const { data, error } = await supabase
        .from("users")
        .select("id_users, activation_code, activation_expires")
        .eq("username", String(username))
        .limit(1)
        .single();
      if (error) return { ok: false, message: "Error consultando usuario" };
      if (!data) return { ok: false, message: "Usuario no encontrado" };
      const u = data;
      if (!u.activation_code || String(u.activation_code) !== String(code)) {
        return { ok: false, message: "Código inválido" };
      }
      if (
        u.activation_expires &&
        new Date(u.activation_expires).getTime() < Date.now()
      ) {
        return { ok: false, message: "Código expirado" };
      }
      const { error: uperr } = await supabase
        .from("users")
        .update({
          is_verified: 1,
          activation_code: null,
          activation_expires: null,
        })
        .eq("id_users", u.id_users);
      if (uperr)
        return { ok: false, message: "Error activando la cuenta (RLS?)" };
      return { ok: true };
    } catch (e) {
      return { ok: false, message: e?.message || "Error activando cuenta" };
    }
  }
  const rows = await query(
    `SELECT id_users AS id, activation_code, activation_expires FROM users WHERE username = ?`,
    [username]
  );
  if (!rows.length) return { ok: false, message: "Usuario no encontrado" };
  const u = rows[0];
  if (!u.activation_code || String(u.activation_code) !== String(code)) {
    return { ok: false, message: "Código inválido" };
  }
  if (
    u.activation_expires &&
    new Date(u.activation_expires).getTime() < Date.now()
  ) {
    return { ok: false, message: "Código expirado" };
  }
  await execute(
    `UPDATE users SET is_verified = 1, activation_code = NULL, activation_expires = NULL WHERE id_users = ?`,
    [u.id]
  );
  return { ok: true };
}

async function sendActivationEmail(email, name, code) {
  try {
    // Prioridad 1: Usar Tauri (backend de Rust con Gmail configurado)
    if (isTauri()) {
      try {
        await invoke("send_activation_email", { to: email, name, code });
        return { ok: true };
      } catch (e) {
        return {
          ok: false,
          message: `Error enviando email: ${e?.toString?.() || e?.message || String(e)}`,
        };
      }
    }
    
    // Prioridad 2: Webhook personalizado (si está configurado) - INCLUSO EN DEV
    const url = import.meta.env?.VITE_MAIL_WEBHOOK_URL;
    if (url) {
      try {
        const response = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            to: email,
            subject: "Activa tu cuenta",
            template: "activation",
            name,
            code,
          }),
        });
        
        if (response.ok) {
          return { ok: true };
        }
        
        const errorText = await response.text();
        return { ok: false, message: `Webhook error: ${errorText}` };
      } catch (e) {
        return { ok: false, message: e?.message || String(e) };
      }
    }
    
    // Prioridad 3: Modo desarrollo sin webhook - simular
    if (import.meta.env.DEV) {
      return { ok: true, message: 'Email simulado en modo desarrollo' };
    }
    
    return { ok: false, message: "No hay servicio de email configurado. Añade VITE_MAIL_WEBHOOK_URL en .env" };
  } catch (e) {
    return {
      ok: false,
      message: e?.message || "Fallo desconocido al enviar correo",
    };
  }
}



async function sendResetEmail(email, name, code) {
  try {
    if (isTauri()) {
      try {
        await invoke("send_reset_email", { to: email, name, code });
        return { ok: true };
      } catch (e) {
        return {
          ok: false,
          message: e?.toString?.() || e?.message || String(e),
        };
      }
    }
    const url = import.meta.env?.VITE_MAIL_WEBHOOK_URL;
    if (url) {
      try {
        await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            to: email,
            subject: "Recupera tu contraseña",
            template: "reset",
            name,
            code,
          }),
        });
        return { ok: true };
      } catch (e) {
        return { ok: false, message: e?.message || String(e) };
      }
    }
    return { ok: false, message: "Webhook no configurado" };
  } catch (e) {
    return {
      ok: false,
      message: e?.message || "Fallo desconocido al enviar correo",
    };
  }
}

async function ensureResetColumns() {
  try {
    const cols = await query(`PRAGMA table_info(users)`);
    const names = cols.map((c) => c.name || c["name"]);
    if (!names.includes("reset_code")) {
      await execute(`ALTER TABLE users ADD COLUMN reset_code TEXT`);
    }
    if (!names.includes("reset_expires")) {
      await execute(`ALTER TABLE users ADD COLUMN reset_expires TEXT`);
    }
  } catch {}
}

export async function requestPasswordReset({ identifier }) {
  // identifier puede ser username o email
  const id = String(identifier || "").trim();
  if (!id) return { ok: false, message: "Ingresa usuario o email" };

  // Lógica para Supabase
  if (useSupabase()) {
    const { data: users, error } = await supabase
      .from('users')
      .select('id_users, username, email, full_name')
      .or(`username.eq.${id},email.eq.${id}`)
      .limit(1);

    if (error || !users || !users.length) return { ok: false, message: "Usuario no encontrado" };
    
    const u = users[0];
    const code = String(Math.floor(100000 + Math.random() * 900000));
    const expiresIso = new Date(Date.now() + 30 * 60 * 1000).toISOString();
    
    const { error: updateError } = await supabase
      .from('users')
      .update({ reset_code: code, reset_expires: expiresIso })
      .eq('id_users', u.id_users);

    if (updateError) return { ok: false, message: "Error al generar código" };

    let mail_ok = false, mail_error = "";
    if (u.email) {
      const m = await sendResetEmail(u.email, u.full_name || u.username, code);
      mail_ok = !!m?.ok;
      mail_error = m?.message || "";
    }
    return { ok: true, reset_code: code, mail_ok, mail_error };
  }

  if (!isTauri()) {
    const users = JSON.parse(localStorage.getItem("dev_users") || "[]");
    const idx = users.findIndex(
      (u) => u.username === id || (u.email || "") === id
    );
    if (idx < 0) return { ok: false, message: "Usuario no encontrado" };
    const code = String(Math.floor(100000 + Math.random() * 900000));
    const expiresIso = new Date(Date.now() + 30 * 60 * 1000).toISOString();
    users[idx].reset_code = code;
    users[idx].reset_expires = expiresIso;
    localStorage.setItem("dev_users", JSON.stringify(users));
    const email = users[idx].email;
    let mail_ok = false,
      mail_error = "";
    if (email) {
      const m = await sendResetEmail(
        email,
        users[idx].full_name || users[idx].username,
        code
      );
      mail_ok = !!m?.ok;
      mail_error = m?.message || "";
    }
    return { ok: true, reset_code: code, mail_ok, mail_error };
  }
  
  // Fallback para SQLite local (si estuviera implementado)
  await ensureResetColumns();
  const rows = await query(
    `SELECT id_users AS id, username, email, full_name FROM users WHERE username = ? OR email = ? LIMIT 1`,
    [id, id]
  );
  if (!rows.length) return { ok: false, message: "Usuario no encontrado" };
  const u = rows[0];
  const code = String(Math.floor(100000 + Math.random() * 900000));
  const expiresIso = new Date(Date.now() + 30 * 60 * 1000).toISOString();
  await execute(
    `UPDATE users SET reset_code = ?, reset_expires = ? WHERE id_users = ?`,
    [code, expiresIso, u.id]
  );
  let mail_ok = false,
    mail_error = "";
  if (u.email) {
    const m = await sendResetEmail(u.email, u.full_name || u.username, code);
    mail_ok = !!m?.ok;
    mail_error = m?.message || "";
  }
  return { ok: true, reset_code: code, mail_ok, mail_error };
}

export async function resetPassword({ username, code, new_password }) {
  const userKey = String(username || "").trim();
  const codeKey = String(code || "").trim();
  const npw = String(new_password || "");

  if (!userKey || !codeKey || !npw) {
    return { ok: false, message: "Faltan datos" };
  }

  // Lógica para Supabase
  if (useSupabase()) {
    const { data: users, error } = await supabase
      .from('users')
      .select('id_users, reset_code, reset_expires')
      .or(`username.eq.${userKey},email.eq.${userKey}`)
      .limit(1);

    if (error || !users || !users.length) return { ok: false, message: "Usuario no encontrado" };
    
    const u = users[0];
    if (String(u.reset_code) !== codeKey) {
      return { ok: false, message: "Código inválido" };
    }
    
    const exp = new Date(u.reset_expires || 0).getTime();
    if (Date.now() > exp) {
      return { ok: false, message: "El código ha expirado" };
    }

    const hashed = await hashPassword(npw);
    const { error: updateError } = await supabase
      .from('users')
      .update({ 
        password: hashed,
        reset_code: null,
        reset_expires: null
      })
      .eq('id_users', u.id_users);

    if (updateError) return { ok: false, message: "Error al actualizar contraseña" };
    return { ok: true };
  }

  if (!isTauri()) {
    const users = JSON.parse(localStorage.getItem("dev_users") || "[]");
    const idx = users.findIndex(
      (u) => u.username === userKey || (u.email || "") === userKey
    );
    if (idx < 0) return { ok: false, message: "Usuario no encontrado" };
    const u = users[idx];
    if (String(u.reset_code) !== codeKey)
      return { ok: false, message: "Código inválido" };
    const exp = new Date(u.reset_expires || 0).getTime();
    if (Date.now() > exp) return { ok: false, message: "El código ha expirado" };
    u.password = await hashPassword(npw); // Simulado
    u.reset_code = null;
    u.reset_expires = null;
    localStorage.setItem("dev_users", JSON.stringify(users));
    return { ok: true };
  }
  
  // Fallback para SQLite local
  await ensureResetColumns();
  const rows = await query(
    `SELECT id_users AS id, reset_code, reset_expires FROM users WHERE username = ? OR email = ? LIMIT 1`,
    [userKey, userKey]
  );
  if (!rows.length) return { ok: false, message: "Usuario no encontrado" };
  const u = rows[0];
  if (String(u.reset_code) !== codeKey)
    return { ok: false, message: "Código inválido" };
  const exp = new Date(u.reset_expires || 0).getTime();
  if (Date.now() > exp) return { ok: false, message: "El código ha expirado" };
  const hashed = await hashPassword(npw);
  await execute(
    `UPDATE users SET password = ?, reset_code = NULL, reset_expires = NULL WHERE id_users = ?`,
    [hashed, u.id]
  );
  return { ok: true };
}

export async function resendActivation({ username }) {
  const userKey = String(username || "").trim();
  if (!userKey) return { ok: false, message: "Ingresa el usuario" };
  if (!isTauri() && !useSupabase()) {
    const users = JSON.parse(localStorage.getItem("dev_users") || "[]");
    const idx = users.findIndex((u) => u.username === userKey);
    if (idx < 0) return { ok: false, message: "Usuario no encontrado" };
    const code = String(Math.floor(100000 + Math.random() * 900000));
    const expiresIso = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
    users[idx].activation_code = code;
    users[idx].activation_expires = expiresIso;
    localStorage.setItem("dev_users", JSON.stringify(users));
    let mail_ok = false,
      mail_error = "";
    const email = users[idx].email;
    if (email) {
      const m = await sendActivationEmail(
        email,
        users[idx].full_name || users[idx].username,
        code
      );
      mail_ok = !!m?.ok;
      mail_error = m?.message || "";
    }
    return { ok: true, activation_code: code, mail_ok, mail_error };
  }
  if (useSupabase()) {
    try {
      const { data, error } = await supabase
        .from("users")
        .select("id_users, email, full_name, username")
        .eq("username", userKey)
        .limit(1)
        .single();
      if (error) return { ok: false, message: "Usuario no encontrado" };
      if (!data) return { ok: false, message: "Usuario no encontrado" };
      const u = data;
      const code = String(Math.floor(100000 + Math.random() * 900000));
      const expiresIso = new Date(
        Date.now() + 24 * 60 * 60 * 1000
      ).toISOString();
      const { error: uperr } = await supabase
        .from("users")
        .update({ activation_code: code, activation_expires: expiresIso })
        .eq("id_users", u.id_users);
      if (uperr)
        return { ok: false, message: "Error actualizando código (RLS?)" };
      let mail_ok = false,
        mail_error = "";
      if (u.email) {
        const m = await sendActivationEmail(
          u.email,
          u.full_name || u.username,
          code
        );
        mail_ok = !!m?.ok;
        mail_error = m?.message || "";
      }
      return { ok: true, activation_code: code, mail_ok, mail_error };
    } catch (e) {
      return {
        ok: false,
        message: e?.message || "Error reenviando activación",
      };
    }
  }
  await ensureActivationColumns();
  const rows = await query(
    `SELECT id_users AS id, email, full_name, username FROM users WHERE username = ?`,
    [userKey]
  );
  if (!rows.length) return { ok: false, message: "Usuario no encontrado" };
  const u = rows[0];
  const code = String(Math.floor(100000 + Math.random() * 900000));
  const expiresIso = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
  await execute(
    `UPDATE users SET activation_code = ?, activation_expires = ? WHERE id_users = ?`,
    [code, expiresIso, u.id]
  );
  let mail_ok = false,
    mail_error = "";
  if (u.email) {
    const m = await sendActivationEmail(
      u.email,
      u.full_name || u.username,
      code
    );
    mail_ok = !!m?.ok;
    mail_error = m?.message || "";
  }
  return { ok: true, activation_code: code, mail_ok, mail_error };
}
